import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import {
  BILLING_PRODUCTS,
  compareBillingFeature,
} from "@polifonia/interlude/billingConstants";
import type { FeatureLimit } from "@polifonia/interlude/models";
import type { muiTheme } from "@polifonia/theme";
import { groupBy } from "es-toolkit/array";
import { mapValues } from "es-toolkit/object";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { useEnableBillingUsageExport } from "@/features/billing/useEnableBillingUsageExport";
import { useInactiveAiAgentFeatures } from "@/features/billing/useInactiveAiAgentFeatures";
import { interlude } from "@/interlude";
import { BillingMonthlyReportToggle } from "@/pages/billing/v2/components/BillingMonthlyReportToggle";
import { ExportUsageButton } from "@/pages/billing/v2/components/ExportUsageButton";
import { ProductUsageCard } from "@/pages/billing/v2/tabs/overview/ProductUsageCard";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "32px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const FeatureUsageBreakdown: React.FC =
  function FeatureUsageBreakdown() {
    const { t } = useTranslation();
    const featureLimitsQuery = interlude.billing.useGetFeatureLimits();
    const enableExport = useEnableBillingUsageExport();
    const inactiveFeatures = useInactiveAiAgentFeatures();

    const groupedFeatures = useMemo((): Record<string, Array<FeatureLimit>> => {
      if (!featureLimitsQuery.isSuccess) return {};
      const grouped = groupBy(
        featureLimitsQuery.data.featureLimits.filter(
          (f) => !inactiveFeatures.has(f.feature),
        ),
        (f) => f.product,
      );
      return mapValues(grouped, (features) =>
        features.toSorted((a, b) =>
          compareBillingFeature(a.feature, b.feature),
        ),
      );
    }, [
      featureLimitsQuery.isSuccess,
      featureLimitsQuery.data?.featureLimits,
      inactiveFeatures,
    ]);

    return (
      <Box sx={styles.root}>
        <Box sx={styles.header}>
          <Typography variant="h2">
            {t("billing.featureUsageBreakdown.title")}
          </Typography>
          <Stack direction="row" gap="8px">
            <BillingMonthlyReportToggle />
            {enableExport ? <ExportUsageButton /> : null}
          </Stack>
        </Box>
        {featureLimitsQuery.isPending ? (
          <Stack alignItems="center" py="48px">
            <CircularProgress />
          </Stack>
        ) : (
          <Box sx={styles.grid}>
            {BILLING_PRODUCTS.map((product) => {
              const features = groupedFeatures[product];
              if (!features || features.length === 0) return null;
              return (
                <ProductUsageCard
                  key={product}
                  product={product}
                  features={features}
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  };
